import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Send, Loader2, Phone, Mail, Instagram } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const serviceOptions = [
  "Network Solutions",
  "Structured Cabling",
  "Security Cameras",
  "Cable Management",
  "Rack and Stack",
  "IT Services",
  "Printers",
  "Data Backup & Drive Cloning",
  "Microsoft 365 & Google Workspace",
  "Smart Thermostat Installation",
  "Website Design & Development",
  "Application Support",
  "Other",
];

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", service: "", message: "" });
  const [loading, setLoading] = useState(false);

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.service || !form.message) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/inquiries`, form);
      toast.success("Thanks! We'll get back to you within 24 hours.");
      setForm({ name: "", email: "", phone: "", service: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again or call us.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="py-24 sm:py-32">
      <div className="container mx-auto px-6 sm:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5">
            <span className="text-sm font-semibold uppercase tracking-widest text-blue-600">Get in Touch</span>
            <h2 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter text-slate-950 mt-4">
              Request a free quote
            </h2>
            <p className="text-lg text-slate-600 mt-5 leading-relaxed">
              Tell us what you need and we'll get back to you within 24 hours with a no-obligation quote.
            </p>

            <div className="mt-10 space-y-5">
              <a href="tel:+19055985264" className="flex items-center gap-4 group" data-testid="contact-phone">
                <div className="h-12 w-12 border border-slate-200 flex items-center justify-center group-hover:bg-blue-600 group-hover:border-blue-600 transition-colors">
                  <Phone size={20} className="text-blue-600 group-hover:text-white transition-colors" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-slate-400">Call us</div>
                  <div className="font-semibold text-slate-950">905.598.5264</div>
                </div>
              </a>
              <a href="mailto:support@ziditsolutions.ca" className="flex items-center gap-4 group" data-testid="contact-email">
                <div className="h-12 w-12 border border-slate-200 flex items-center justify-center group-hover:bg-blue-600 group-hover:border-blue-600 transition-colors">
                  <Mail size={20} className="text-blue-600 group-hover:text-white transition-colors" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-slate-400">Email us</div>
                  <div className="font-semibold text-slate-950">support@ziditsolutions.ca</div>
                </div>
              </a>
              <a href="https://instagram.com/ziditsolutions" target="_blank" rel="noopener noreferrer" className="flex items-center gap-4 group" data-testid="contact-instagram">
                <div className="h-12 w-12 border border-slate-200 flex items-center justify-center group-hover:bg-blue-600 group-hover:border-blue-600 transition-colors">
                  <Instagram size={20} className="text-blue-600 group-hover:text-white transition-colors" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-slate-400">Follow us</div>
                  <div className="font-semibold text-slate-950">@ziditsolutions</div>
                </div>
              </a>
            </div>
          </div>

          <div className="lg:col-span-7">
            <form
              onSubmit={submit}
              data-testid="contact-form"
              className="bg-white border border-slate-200 p-8 sm:p-10 space-y-6"
            >
              <div className="grid sm:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="name">Full Name *</Label>
                  <Input
                    id="name"
                    data-testid="input-name"
                    value={form.name}
                    onChange={(e) => update("name", e.target.value)}
                    placeholder="John Smith"
                    className="rounded-none border-slate-300"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone</Label>
                  <Input
                    id="phone"
                    data-testid="input-phone"
                    value={form.phone}
                    onChange={(e) => update("phone", e.target.value)}
                    placeholder="+1 (000) 000-0000"
                    className="rounded-none border-slate-300"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  data-testid="input-email"
                  value={form.email}
                  onChange={(e) => update("email", e.target.value)}
                  placeholder="you@company.com"
                  className="rounded-none border-slate-300"
                />
              </div>

              <div className="space-y-2">
                <Label>Service Needed *</Label>
                <Select value={form.service} onValueChange={(v) => update("service", v)}>
                  <SelectTrigger data-testid="select-service" className="rounded-none border-slate-300">
                    <SelectValue placeholder="Select a service" />
                  </SelectTrigger>
                  <SelectContent>
                    {serviceOptions.map((s) => (
                      <SelectItem key={s} value={s} data-testid={`service-option-${s}`}>
                        {s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="message">Message *</Label>
                <Textarea
                  id="message"
                  data-testid="input-message"
                  value={form.message}
                  onChange={(e) => update("message", e.target.value)}
                  placeholder="Tell us about your project or requirements..."
                  rows={5}
                  className="rounded-none border-slate-300 resize-none"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                data-testid="submit-inquiry-btn"
                className="w-full inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white font-semibold px-8 py-4 transition-colors"
              >
                {loading ? (
                  <>
                    <Loader2 size={18} className="animate-spin" /> Sending...
                  </>
                ) : (
                  <>
                    Send Request <Send size={18} />
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};
